import React from 'react';
import { Box, Typography, Paper, Stack, Chip } from '@mui/material';
import { Person, Email, Phone, Business, WorkspacePremium, Place, VerifiedUser } from '@mui/icons-material';
import { ROLES } from '../../constants/roles';
import { CREW_CLEARANCE_CONFIG } from '../../utils/crewClearance';

const GENDER_LABELS = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other'
};

const formatWorkLocation = (wl) => {
  if (!wl) return '';
  const parts = [
    wl.addressLine1,
    wl.addressLine2,
    [wl.city, wl.stateRegion].filter(Boolean).join(', '),
    wl.postalCode,
    wl.country
  ].filter(Boolean);
  return parts.join(' · ');
};

const ProfileView = ({ data }) => {
  if (!data) return null;

  const gender =
    data.userId?.gender ||
    data.profileCompletion?.gender;
  const workLocation =
    data.userId?.workLocation ||
    data.profileCompletion?.workLocation;
  const locationText = formatWorkLocation(workLocation);
  const roleForClearance = data.userId?.role || data.requestedRole;
  const clearanceValues = CREW_CLEARANCE_CONFIG.map(({ field, label }) => ({
    field,
    label,
    value: data.userId?.[field] ?? data.profileCompletion?.[field]
  }));

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Profile Information</Typography>
      <Stack spacing={2} mt={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Person color="action" />
          <Box>
            <Typography variant="caption" color="text.secondary">Name</Typography>
            <Typography variant="body1">
              {data.userId?.firstName || data.firstName} {data.userId?.lastName || data.lastName}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Email color="action" />
          <Box>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography variant="body1">{data.userId?.email || data.email}</Typography>
          </Box>
        </Box>

        {gender && (
          <Box display="flex" alignItems="center" gap={2}>
            <Person color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">Gender</Typography>
              <Typography variant="body1">{GENDER_LABELS[gender] || gender}</Typography>
            </Box>
          </Box>
        )}

        {(data.userId?.phone || data.profileCompletion?.phone) && (
          <Box display="flex" alignItems="center" gap={2}>
            <Phone color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">Phone</Typography>
              <Typography variant="body1">{data.userId?.phone || data.profileCompletion?.phone}</Typography>
            </Box>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={2}>
          <WorkspacePremium color="action" />
          <Box>
            <Typography variant="caption" color="text.secondary">Role</Typography>
            <Chip 
              label={data.requestedRole?.replace(/_/g, ' ')} 
              size="small" 
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>

        {roleForClearance === ROLES.CREW &&
          clearanceValues
            .filter(({ value }) => value === true || value === false)
            .map(({ label, value }) => (
              <Box key={label} display="flex" alignItems="center" gap={2}>
                <VerifiedUser color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body1">{value ? 'Yes' : 'No'}</Typography>
                </Box>
              </Box>
            ))}

        {(data.userId?.department || data.profileCompletion?.department) && (
          <Box display="flex" alignItems="center" gap={2}>
            <Business color="action" />
            <Box>
              <Typography variant="caption" color="text.secondary">Department</Typography>
              <Typography variant="body1">{data.userId?.department || data.profileCompletion?.department}</Typography>
            </Box>
          </Box>
        )}

        {locationText && (
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Place color="action" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">Base work / reporting location</Typography>
              <Typography variant="body1">{locationText}</Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default ProfileView;
